import { Fragment, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import Grid from '@mui/material/Grid'

import { RootState } from '../../../appReducer'
import { PracticeLocationReschedulingConfiguration } from '../../../models/v2/Practice'
import { fetchPracticeLocationsSchedulingDataWithAgentModule } from '../../../modules/practices/actions'
import CustomMultiselectField from '../../../modules/shared/custom-fields/CustomMultiselectField'
import InfoMessage from '../../shared/info-message/info-message'
import Paginator from '../../shared/Paginator'

import AllowSidebookingCheckbox from './AllowSidebookingCheckbox'
import {
    CANCELLATIONS_VALUES,
    CONCURRENCY_NUM_ARRAY,
    DAYS_PRIOR_VALUES,
    HOURS_PRIOR_VALUES,
    PRIOR_UNIT_VALUES,
    RESCHEDULING_VALUES,
} from './constants'
import { updatePracticeLocationReschedulingConfiguration } from './v2actions'

import './CancelRescheduleSettingsLocationsList.sass'

export type CancelRescheduleSettingsLocationsListProps = {
    practiceId: string
    isPending: boolean
    searchPage: number
    setSearchPage: (page: number) => void
}

const moduleName = 'cancel-reschedule-settings-locations-list'

type Props = CancelRescheduleSettingsLocationsListProps

const CancelRescheduleSettingsLocationsList = ({ practiceId, searchPage, setSearchPage }: Props) => {
    const loading = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceLocationsData?.[practiceId]?.loading || false,
    )

    const list = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceLocationsData?.[practiceId]?.list,
    )
    const pagination = useSelector(
        (state: RootState) =>
            state.v2.reschedulingConfiguration.practiceLocationsData?.[practiceId]?.pagination || null,
    )

    const connectedPracticeLocations = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.connectedPracticeLocations?.[practiceId] || [],
    )

    const settings = useSelector((state: RootState) => state.v2.reschedulingConfiguration.settings?.[practiceId] || [])

    const practiceLocationsAgentModules = useSelector(
        (state: RootState) => state.practices.practiceLocationsAgentModules[practiceId] || [],
    )

    const practiceSettingsPracticeId = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.practice_id,
    )
    const practiceSettingsCancelAppointment = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.cancel_appointment,
    )
    const practiceSettingsRescheduleAppointment = useSelector(
        (state: RootState) =>
            state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.reschedule_appointment,
    )
    const practiceSettingsConcurrencyNum = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.concurrency_num,
    )
    const practiceSettingsReschedulePrior = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.reschedule_prior,
    )

    const practiceSettingsReschedulePriorUnit = useSelector(
        (state: RootState) =>
            state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.reschedule_prior_unit,
    )

    const practiceSettingsCancelPrior = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.cancel_prior,
    )

    const practiceSettingsCancelPriorUnit = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.cancel_prior_unit,
    )

    const hasGlobalSettings = Boolean(practiceSettingsPracticeId)

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(fetchPracticeLocationsSchedulingDataWithAgentModule(practiceId))
    }, [dispatch, practiceId])

    const onUpdateSettings = (
        field: string,
        value: string | number | boolean,
        settings: PracticeLocationReschedulingConfiguration | undefined,
        locationId: string,
    ) => {
        if (settings?.practice_location_id) {
            const reschedule_prior =
                field === 'reschedule_prior_unit' && value === 'days' && settings?.reschedule_prior > 8
                    ? 1
                    : settings.reschedule_prior
            const cancel_prior =
                field === 'cancel_prior_unit' && value === 'days' && settings?.cancel_prior > 8
                    ? 1
                    : settings.cancel_prior
            const updatedSettings = {
                ...settings,
                reschedule_prior,
                cancel_prior,
                practice_location_id: locationId,
                [field]: value,
            }

            dispatch(updatePracticeLocationReschedulingConfiguration(practiceId, locationId, [updatedSettings], false))
        } else {
            const createdSettings =
                field === 'use_global_settings' && value === false && hasGlobalSettings
                    ? {
                          ...{
                              practice_location_id: locationId,
                              cancel_appointment: practiceSettingsCancelAppointment,
                              reschedule_appointment: practiceSettingsRescheduleAppointment,
                              reschedule_prior: practiceSettingsReschedulePrior,
                              reschedule_prior_unit: practiceSettingsReschedulePriorUnit,
                              cancel_prior: practiceSettingsCancelPrior,
                              cancel_prior_unit: practiceSettingsCancelPriorUnit,
                              concurrency_num: practiceSettingsConcurrencyNum,
                              use_global_settings: false,
                          },
                      }
                    : {
                          ...{
                              practice_location_id: locationId,
                              cancel_appointment: 'allow',
                              reschedule_appointment: 'allow',
                              reschedule_prior: 1,
                              reschedule_prior_unit: 'days' as 'days' | 'hours',
                              cancel_prior: 1,
                              cancel_prior_unit: 'days' as 'days' | 'hours',
                              concurrency_num: 1,
                              use_global_settings: false,
                          },
                          [field]: value,
                      }
            dispatch(updatePracticeLocationReschedulingConfiguration(practiceId, locationId, [createdSettings], true))
        }
    }

    const handlePageChange = (nextPage: number) => {
        setSearchPage(nextPage)
    }

    const renderRows = () => {
        return (
            <>
                {Boolean(list && list.length > 0) ? (
                    list?.map(location => {
                        const locationSetting = settings.find(s => s.practice_location_id === location.id)
                        const connectedLocation = connectedPracticeLocations?.find(l => l.id === location?.id)
                        const useGlobalSettingsChecked =
                            locationSetting?.use_global_settings === undefined
                                ? hasGlobalSettings
                                : locationSetting?.use_global_settings

                        return (
                            <Fragment key={location.id}>
                                <Grid
                                    item={true}
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={4}
                                    className={`${moduleName}__table-item ${moduleName}__table-item-first`}
                                    style={connectedLocation ? { borderBottom: 'none' } : undefined}
                                >
                                    {location.name}
                                </Grid>
                                {connectedLocation ? (
                                    <>
                                        <Grid
                                            item={true}
                                            xs={6}
                                            sm={3}
                                            md={4}
                                            lg={3}
                                            className={`${moduleName}__table-item details`}
                                            style={{ borderBottom: 'none' }}
                                        >
                                            <div className={`${moduleName}__role-picker`}>
                                                <CustomMultiselectField
                                                    items={CANCELLATIONS_VALUES}
                                                    maxSelected={1}
                                                    disabled={useGlobalSettingsChecked}
                                                    selectedItems={[
                                                        locationSetting?.cancel_appointment === undefined ||
                                                        useGlobalSettingsChecked
                                                            ? hasGlobalSettings
                                                                ? practiceSettingsCancelAppointment
                                                                : ''
                                                            : locationSetting?.cancel_appointment,
                                                    ]}
                                                    disabledOptions={
                                                        location.is_online_scheduling_writeback_enabled === false
                                                    }
                                                    withTooltipIcon={
                                                        location.is_online_scheduling_writeback_enabled === false
                                                    }
                                                    keyProperty="value"
                                                    displayProperty="display"
                                                    placeholder=""
                                                    label="Cancellations"
                                                    onSelectElement={(values: string[]) => {
                                                        onUpdateSettings(
                                                            'cancel_appointment',
                                                            values[0],
                                                            locationSetting,
                                                            location.id,
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </Grid>
                                        <Grid
                                            item={true}
                                            xs={12}
                                            sm={6}
                                            md={3}
                                            lg={2}
                                            xl={2}
                                            className={`${moduleName}__table-item details`}
                                            style={{ borderBottom: 'none' }}
                                        >
                                            <div className={`${moduleName}__drop-down`} style={{ width: '100%' }}>
                                                <CustomMultiselectField
                                                    items={PRIOR_UNIT_VALUES}
                                                    disabled={useGlobalSettingsChecked}
                                                    maxSelected={1}
                                                    selectedItems={[
                                                        locationSetting?.cancel_prior_unit === undefined ||
                                                        useGlobalSettingsChecked
                                                            ? hasGlobalSettings
                                                                ? practiceSettingsCancelPrior?.toString()
                                                                : ''
                                                            : locationSetting?.cancel_prior_unit?.toString(),
                                                    ]}
                                                    keyProperty="value"
                                                    displayProperty="display"
                                                    placeholder=""
                                                    label="Allow Cancellation Changes Unit"
                                                    onSelectElement={(values: string[]) => {
                                                        onUpdateSettings(
                                                            'cancel_prior_unit',
                                                            values[0],
                                                            locationSetting,
                                                            location.id,
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </Grid>

                                        <Grid
                                            item={true}
                                            className={`${moduleName}__table-item details`}
                                            xs={6}
                                            sm={3}
                                            md={3}
                                            lg={2}
                                            xl={2}
                                            style={{ borderBottom: 'none' }}
                                        >
                                            <div className={`${moduleName}__role-picker`}>
                                                <CustomMultiselectField
                                                    items={
                                                        locationSetting?.cancel_prior_unit === 'days'
                                                            ? DAYS_PRIOR_VALUES
                                                            : HOURS_PRIOR_VALUES
                                                    }
                                                    disabled={useGlobalSettingsChecked}
                                                    maxSelected={1}
                                                    selectedItems={[
                                                        locationSetting?.cancel_prior === undefined ||
                                                        useGlobalSettingsChecked
                                                            ? hasGlobalSettings
                                                                ? practiceSettingsCancelPrior?.toString()
                                                                : ''
                                                            : locationSetting?.cancel_prior?.toString(),
                                                    ]}
                                                    keyProperty="value"
                                                    displayProperty="display"
                                                    placeholder=""
                                                    label="Allow Cancellation Changes"
                                                    onSelectElement={(values: string[]) => {
                                                        onUpdateSettings(
                                                            'cancel_prior',
                                                            Number(values[0]),
                                                            locationSetting,
                                                            location.id,
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </Grid>
                                        {[1, 5].includes(
                                            practiceLocationsAgentModules.find(
                                                p => location.id === p.practice_location_id,
                                            )?.heartbeat_agent_module?.type_id || 0,
                                        ) ? (
                                            <Grid
                                                item={true}
                                                xs={4}
                                                sm={3}
                                                md={2}
                                                lg={1}
                                                className={`${moduleName}__table-item details ${moduleName}__checkbox`}
                                                style={{ borderBottom: 'none' }}
                                            ></Grid>
                                        ) : (
                                            <Grid
                                                item={true}
                                                className={`${moduleName}__table-item`}
                                                sm={3}
                                                md={3}
                                                lg={1}
                                                style={{ borderBottom: 'none' }}
                                            ></Grid>
                                        )}
                                        <Grid
                                            item={true}
                                            xs={12}
                                            sm={12}
                                            md={12}
                                            lg={4}
                                            className={`${moduleName}__table-item-first`}
                                        ></Grid>
                                        <Grid
                                            item={true}
                                            xs={6}
                                            sm={3}
                                            md={4}
                                            lg={3}
                                            className={`${moduleName}__table-item details`}
                                        >
                                            <div className={`${moduleName}__role-picker`}>
                                                <CustomMultiselectField
                                                    items={RESCHEDULING_VALUES}
                                                    disabled={useGlobalSettingsChecked}
                                                    maxSelected={1}
                                                    selectedItems={[
                                                        locationSetting?.reschedule_appointment === undefined ||
                                                        useGlobalSettingsChecked
                                                            ? hasGlobalSettings
                                                                ? practiceSettingsRescheduleAppointment
                                                                : ''
                                                            : locationSetting?.reschedule_appointment,
                                                    ]}
                                                    disabledOptions={
                                                        location.is_online_scheduling_writeback_enabled === false
                                                    }
                                                    withTooltipIcon={
                                                        location.is_online_scheduling_writeback_enabled === false
                                                    }
                                                    keyProperty="value"
                                                    displayProperty="display"
                                                    placeholder=""
                                                    label="Reschedules"
                                                    onSelectElement={(values: string[]) => {
                                                        onUpdateSettings(
                                                            'reschedule_appointment',
                                                            values[0],
                                                            locationSetting,
                                                            location.id,
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </Grid>

                                        <Grid
                                            item={true}
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            lg={2}
                                            xl={2}
                                            className={`${moduleName}__table-item details`}
                                        >
                                            <div className={`${moduleName}__drop-down`} style={{ width: '100%' }}>
                                                <CustomMultiselectField
                                                    items={PRIOR_UNIT_VALUES}
                                                    disabled={useGlobalSettingsChecked}
                                                    maxSelected={1}
                                                    selectedItems={[
                                                        locationSetting?.reschedule_prior_unit === undefined ||
                                                        useGlobalSettingsChecked
                                                            ? hasGlobalSettings
                                                                ? practiceSettingsReschedulePriorUnit?.toString()
                                                                : ''
                                                            : locationSetting?.reschedule_prior_unit?.toString(),
                                                    ]}
                                                    keyProperty="value"
                                                    displayProperty="display"
                                                    placeholder=""
                                                    label="Allow Reschedule Changes Unit"
                                                    onSelectElement={(values: string[]) => {
                                                        onUpdateSettings(
                                                            'reschedule_prior_unit',
                                                            values[0],
                                                            locationSetting,
                                                            location.id,
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </Grid>
                                        <Grid
                                            item={true}
                                            className={`${moduleName}__table-item details`}
                                            xs={6}
                                            sm={3}
                                            md={3}
                                            lg={2}
                                        >
                                            <div className={`${moduleName}__role-picker`}>
                                                <CustomMultiselectField
                                                    items={
                                                        locationSetting?.reschedule_prior_unit === 'days'
                                                            ? DAYS_PRIOR_VALUES
                                                            : HOURS_PRIOR_VALUES
                                                    }
                                                    disabled={useGlobalSettingsChecked}
                                                    maxSelected={1}
                                                    selectedItems={[
                                                        locationSetting?.reschedule_prior === undefined ||
                                                        useGlobalSettingsChecked
                                                            ? hasGlobalSettings
                                                                ? practiceSettingsReschedulePrior?.toString()
                                                                : ''
                                                            : locationSetting?.reschedule_prior?.toString(),
                                                    ]}
                                                    keyProperty="value"
                                                    displayProperty="display"
                                                    placeholder=""
                                                    label="Allow Reschedule Changes"
                                                    onSelectElement={(values: string[]) => {
                                                        onUpdateSettings(
                                                            'reschedule_prior',
                                                            Number(values[0]),
                                                            locationSetting,
                                                            location.id,
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </Grid>
                                        <>
                                            {[1, 5].includes(
                                                practiceLocationsAgentModules.find(
                                                    p => location.id === p.practice_location_id,
                                                )?.heartbeat_agent_module?.type_id || 0,
                                            ) ? (
                                                <AllowSidebookingCheckbox
                                                    useGlobalSettingsChecked={useGlobalSettingsChecked}
                                                    hasGlobalSettings={hasGlobalSettings}
                                                    practiceSettingsConcurrencyNum={practiceSettingsConcurrencyNum}
                                                    onUpdateSettings={onUpdateSettings}
                                                    locationSetting={locationSetting}
                                                    location={location}
                                                />
                                            ) : (
                                                <Grid
                                                    item={true}
                                                    className={`${moduleName}__table-item`}
                                                    sm={3}
                                                    md={3}
                                                    lg={1}
                                                    style={{ borderTop: 'none' }}
                                                >
                                                    <div className={`${moduleName}__sidebooking-picker`}>
                                                        <CustomMultiselectField
                                                            items={CONCURRENCY_NUM_ARRAY.map(value => ({
                                                                value: value?.toString(),
                                                                display: `${value}`,
                                                            }))}
                                                            disabled={useGlobalSettingsChecked}
                                                            maxSelected={1}
                                                            selectedItems={[
                                                                locationSetting?.concurrency_num === undefined ||
                                                                useGlobalSettingsChecked
                                                                    ? hasGlobalSettings
                                                                        ? practiceSettingsConcurrencyNum?.toString()
                                                                        : ''
                                                                    : locationSetting?.concurrency_num?.toString(),
                                                            ]}
                                                            keyProperty="value"
                                                            displayProperty="display"
                                                            placeholder=""
                                                            label="Sidebooking"
                                                            onSelectElement={(values: string[]) => {
                                                                onUpdateSettings(
                                                                    'concurrency_num',
                                                                    Number(values[0]),
                                                                    locationSetting,
                                                                    location.id,
                                                                )
                                                            }}
                                                        />
                                                    </div>
                                                </Grid>
                                            )}
                                        </>
                                    </>
                                ) : (
                                    <Grid
                                        item={true}
                                        key={location.id}
                                        className={`${moduleName}__table-item ${moduleName}__info-msg`}
                                        sm={12}
                                        md={12}
                                        lg={8}
                                    >
                                        <InfoMessage isShown={true}>
                                            Practice Location not connected to an Agent.
                                        </InfoMessage>
                                    </Grid>
                                )}
                            </Fragment>
                        )
                    })
                ) : (
                    <div />
                )}
            </>
        )
    }

    return (
        <div className={`${moduleName}`}>
            <Grid className={`${moduleName}__container`} container={true}>
                {renderRows()}
            </Grid>
            {typeof list === 'undefined' && (
                <div className={`no-records`}>
                    <InfoMessage isShown={true}>
                        Please select a location, user(s), date range and click on “GET LOGS” to view configuration
                        logs.
                    </InfoMessage>
                </div>
            )}

            {Boolean(pagination) && pagination?.allRows && pagination?.allRows > 5 ? (
                <div className={`${moduleName}__paginator-wrapper`}>
                    <Paginator currentPage={searchPage} paginationInfo={pagination} selectPage={handlePageChange} />
                </div>
            ) : null}
            {loading && (
                <div className={`${moduleName}__circular-progress-loader-body`}>
                    <CircularProgress
                        className={`${moduleName}__circular-progress-spinner`}
                        size={50}
                        color="primary"
                        variant="indeterminate"
                    />
                </div>
            )}
        </div>
    )
}

export default CancelRescheduleSettingsLocationsList
